import React, { useState, useMemo, useEffect } from "react";
import { api } from "../../../../shared/hooks/api";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import PageLoader from "../../../../shared/components/LoadingPage";
import { formatCurrency, formatInternationalNumber } from "../../../../utils/format";
import {
    ChartContainer,
    ChartHeader,
    ChartTitle,
    ContentWrapper,
    LegendColorBox,
    LegendContainer,
    LegendItem,
    LegendText,
    TitleSection,
    ToggleOption,
    ToggleWrapper,
    TooltipContainer,
    TooltipLabel,
    TooltipValue,
    TotalOrdersCount
} from "./Styled";

interface TotalPriceData {
    material_order_id: string;
    price: number;
    quantity: number;
}

interface SinglePriceData {
    material_order_id: string;
    price: number;
}

interface ChartDataPoint {
    name: string;
    value: number;
}

enum ChartType {
    TOTAL_PRICE = "total_price",
    SINGLE_PRICE = "single_price"
}

const formatYAxisValue = (value: number): string => {
    if (value >= 1000000000) {
        return `${formatInternationalNumber(Math.round((value / 1000000000) * 10) / 10)} tỉ`;
    } else if (value >= 1000000) {
        return `${formatInternationalNumber(Math.round((value / 1000000) * 10) / 10)} triệu`;
    } else if (value >= 100000) {
        return `${formatInternationalNumber(Math.round((value / 100000) * 10) / 10)} trăm`;
    } else if (value >= 1000) {
        return `${formatInternationalNumber(Math.round((value / 1000) * 10) / 10)} nghìn`;
    }
    return value.toString();
};

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <TooltipContainer>
                <TooltipLabel>{`${label}`}</TooltipLabel>
                <TooltipValue color={payload[0].color}>{`${payload[0].name}: ${formatCurrency(payload[0].value)}`}</TooltipValue>
            </TooltipContainer>
        );
    }
    return null;
};

const CustomLegend = (props: any) => {
    const { payload } = props;
    return (
        <LegendContainer>
            {payload.map((entry: any, index: any) => (
                <LegendItem key={index}>
                    <LegendColorBox backgroundColor={entry.color} />
                    <LegendText>{entry.value}</LegendText>
                </LegendItem>
            ))}
        </LegendContainer>
    );
};

const processTotalPriceData = (data: TotalPriceData[]): ChartDataPoint[] => {
    return data.map((item, index) => ({
        name: `Đơn hàng ${index + 1}`,
        value: item.price,
    }));
};

const processSinglePriceData = (data: SinglePriceData[]): ChartDataPoint[] => {
    return data.map((item, index) => ({
        name: `Đơn hàng ${index + 1}`,
        value: item.price,
    }));
};

interface PriceChartProps {
    data: ChartDataPoint[];
    chartName: string;
    totalOrders: number;
    color: string;
    chartType: ChartType;
    setChartType: (type: ChartType) => void;
}

const PriceChart: React.FC<PriceChartProps> = ({ 
    data, 
    chartName, 
    totalOrders, 
    color,
    chartType,
    setChartType
}) => {
    return (
        <ChartContainer>
            <ChartHeader>
                <TitleSection>
                    <ChartTitle>{chartName}</ChartTitle>
                    <TotalOrdersCount>Số lượng đơn hàng: {totalOrders}</TotalOrdersCount>
                </TitleSection>
                <ToggleWrapper>
                    <ToggleOption 
                        isSelected={chartType === ChartType.TOTAL_PRICE}
                        onClick={() => setChartType(ChartType.TOTAL_PRICE)}
                    >
                        Tổng giá mua
                    </ToggleOption>
                    <ToggleOption 
                        isSelected={chartType === ChartType.SINGLE_PRICE}
                        onClick={() => setChartType(ChartType.SINGLE_PRICE)}
                    >
                        Đơn giá
                    </ToggleOption>
                </ToggleWrapper>
            </ChartHeader>
            <ResponsiveContainer width="100%" height={300}>
                <LineChart
                    data={data}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={formatYAxisValue} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend content={<CustomLegend />} />
                    <Line
                        type="monotone"
                        dataKey="value"
                        name={chartName}
                        stroke={color}
                        strokeWidth={2}
                        dot={{ strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </ChartContainer>
    );
};

const PriceStatistics = ({ materialId }: { materialId: string }) => {
    const [chartType, setChartType] = useState<ChartType>(ChartType.TOTAL_PRICE);
    const currentYear = new Date().getFullYear();
    
    const isTotalPriceView = chartType === ChartType.TOTAL_PRICE;
    
    const [{ data: chartData, isLoading, setLocalUrl }, fetchData] = api.get(
        `/materials/${materialId}/price-statistics/?year=${currentYear}${isTotalPriceView ? '&type=total_price' : ''}`
    );
    
    useEffect(() => {
        const newUrl = `/materials/${materialId}/price-statistics/?year=${currentYear}${isTotalPriceView ? '&type=total_price' : ''}`;
        setLocalUrl(newUrl);
    }, [chartType]);
    
    const processedData = useMemo(() => {
        if (!chartData) return [];
        
        if (isTotalPriceView) {
            return chartData.total_price ? processTotalPriceData(chartData.total_price) : [];
        } else {
            return chartData.single_price ? processSinglePriceData(chartData.single_price) : [];
        }
    }, [chartData, isTotalPriceView]);
    
    const totalOrders = useMemo(() => {
        return chartData?.total_material_order || 0;
    }, [chartData]);
    
    if (isLoading) {
        return <PageLoader />;
    }
    
    return (
        <ContentWrapper>
            <PriceChart 
                data={processedData}
                chartName={isTotalPriceView ? "Tổng giá mua (VNĐ)" : "Đơn giá (VNĐ)"}
                totalOrders={totalOrders}
                color={isTotalPriceView ? "#2196F3" : "#FF5722"}
                chartType={chartType}
                setChartType={setChartType}
            />
        </ContentWrapper>
    );
};

export default PriceStatistics; 
